'use client';

import { Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import type { SearchContext } from '@/types/database';

interface SearchContextBannerProps {
  searchContext: SearchContext;
}

export function SearchContextBanner({ searchContext }: SearchContextBannerProps) {
  const router = useRouter();

  // Don't show for browse_all or non-search intents without explanation
  if (
    searchContext.intent === 'browse_all' ||
    searchContext.intent === 'unsupported' ||
    searchContext.intent === 'cannot_parse'
  ) {
    return null;
  }

  const handleClearSearch = () => {
    router.push('/properties');
  };

  const handleRefinement = (refinement: string) => {
    const newQuery = `${searchContext.query} ${refinement}`.trim();
    const params = new URLSearchParams();
    params.set('q', newQuery);
    router.push(`/properties?${params.toString()}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full mb-8"
    >
      {/* Main banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-primary/10 pb-4">
        <div className="flex items-center gap-2 text-sm font-sans">
          <Sparkles className="w-4 h-4 text-secondary flex-shrink-0" />
          <span className="text-muted-foreground">
            Showing{' '}
            <span className="font-bold text-primary">
              {searchContext.resultCount}
            </span>{' '}
            {searchContext.resultCount === 1 ? 'property' : 'properties'}
            {searchContext.explanation && (
              <>
                {' '}
                matching:{' '}
                <span className="text-primary">{searchContext.explanation}</span>
              </>
            )}
            {searchContext.wasRelaxed && searchContext.relaxationNote && (
              <span className="text-muted-foreground/70">
                {' '}
                ({searchContext.relaxationNote})
              </span>
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={handleClearSearch}
          className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
        >
          Clear Search
        </button>
      </div>

      {/* Refinement chips for broad searches */}
      {searchContext.suggestedRefinements.length > 0 && (
        <div className="flex items-center gap-2 pt-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            Refine:
          </span>
          {Array.from(new Set(searchContext.suggestedRefinements)).map((refinement) => (
            <button
              key={refinement}
              type="button"
              onClick={() => handleRefinement(refinement)}
              className={cn(
                'border border-primary/20 rounded-xl px-3 py-1.5',
                'text-[11px] tracking-wider text-muted-foreground',
                'hover:border-secondary hover:text-secondary',
                'transition-all duration-200 cursor-pointer'
              )}
            >
              {refinement}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}
